import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ProposalDocumentBidding,
  buildDeclarationFiles,
  buildProposalStructure,
  downloadDeclarationDocx,
  downloadDeclarationPdf,
  downloadProposalDocx,
  downloadProposalPdf,
  loadProposalDocumentInput,
  yieldToBrowser,
} from 'components/molecules/ai_management_proposal_document';
import { getProposalDocuments } from 'services/actions/ai_management_proposal_documents';
import { BidderFileCategory, uploadBidderFile } from 'services/actions/ai_management_files';
import {
  ProposalResponse,
  registerGeneratedProposal,
} from 'services/actions/ai_management_proposal';

interface Options {
  onRegistered: (data: ProposalResponse) => void;
  onFilesUploaded?: () => void;
}

interface PendingFile {
  blob: Blob;
  filename: string;
  category: BidderFileCategory;
  uploadedId: string | null;
}

export const GENERATION_ERROR =
  'Não foi possível gerar a proposta. Seus dados estão salvos. Tente novamente.';

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status.
const failed = (res: { status?: number } | undefined) => !res || (res.status ?? 200) >= 400;

// Cada arquivo sobe separado em "Arquivos gerados pelo licitante"; a geração só é registrada
// quando todos subirem. Os blobs ficam guardados para "Tentar de novo" reenviar só os que falharam.
export function useProposalGeneration(
  biddingId: number | null,
  bidding: ProposalDocumentBidding,
  { onRegistered, onFilesUploaded }: Options,
) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedCount, setFailedCount] = useState(0);
  const pending = useRef<PendingFile[]>([]);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    pending.current = [];
    setError(null);
    setFailedCount(0);
  }, [biddingId]);

  const buildFiles = useCallback(async (): Promise<PendingFile[]> => {
    const input = await loadProposalDocumentInput(biddingId as number, bidding);
    await yieldToBrowser();
    const structure = buildProposalStructure(input);
    const pdf = await downloadProposalPdf(structure);
    const docx = await downloadProposalDocx(structure);
    const arquivos = [pdf, docx];

    // Cada declaração de proposta confirmada sai no próprio arquivo, em PDF e DOCX.
    const res = await getProposalDocuments(biddingId as number);
    if (failed(res) || !Array.isArray(res?.data?.declaracoes)) throw new Error('declaracoes');
    const declaracoes = buildDeclarationFiles(
      res.data.declaracoes,
      bidding,
      input.company,
      input.geradoEm,
    );
    // eslint-disable-next-line no-restricted-syntax
    for (const declaracao of declaracoes) {
      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
      // eslint-disable-next-line no-await-in-loop
      arquivos.push(await downloadDeclarationPdf(declaracao.estrutura, declaracao.nomeBase));
      // eslint-disable-next-line no-await-in-loop
      arquivos.push(await downloadDeclarationDocx(declaracao.estrutura, declaracao.nomeBase));
    }

    return arquivos.map((file) => ({
      ...file,
      category: 'proposta' as BidderFileCategory,
      uploadedId: null,
    }));
  }, [biddingId, bidding]);

  const uploadAndRegister = useCallback(async () => {
    const id = biddingId as number;
    // Em sequência: PDFs grandes em paralelo somariam o pico de memória.
    // eslint-disable-next-line no-restricted-syntax
    for (const file of pending.current) {
      if (!file.uploadedId) {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadBidderFile(
          id,
          'preparar_proposta',
          file.blob,
          file.filename,
          file.category,
        ).catch(() => undefined);
        if (!failed(res) && res?.data?.file?.id) file.uploadedId = res.data.file.id;
      }
    }

    const faltando = pending.current.filter((file) => !file.uploadedId).length;
    if (pending.current.some((file) => file.uploadedId)) onFilesUploaded?.();
    if (faltando > 0) {
      setFailedCount(faltando);
      setError(GENERATION_ERROR);
      return;
    }

    const res = await registerGeneratedProposal(
      id,
      pending.current.map((file) => file.uploadedId as string),
    ).catch(() => undefined);
    if (failed(res) || !res?.data) {
      setFailedCount(0);
      setError(GENERATION_ERROR);
      return;
    }
    pending.current = [];
    setFailedCount(0);
    setError(null);
    onRegistered(res.data);
  }, [biddingId, onRegistered, onFilesUploaded]);

  const run = useCallback(
    async (rebuild: boolean) => {
      if (!biddingId || generating) return;
      setGenerating(true);
      setError(null);
      try {
        if (rebuild || pending.current.length === 0) pending.current = await buildFiles();
        await uploadAndRegister();
      } catch (e) {
        if (mounted.current) setError(GENERATION_ERROR);
      } finally {
        if (mounted.current) setGenerating(false);
      }
    },
    [biddingId, generating, buildFiles, uploadAndRegister],
  );

  const generate = useCallback(() => run(true), [run]);
  const retry = useCallback(() => run(false), [run]);

  return { generating, error, failedCount, generate, retry };
}
